import { is } from 'date-fns/locale';
import React, { useEffect, useMemo, useState } from 'react'
import { CustomDialog, NormalSwitch } from '../../../components/StyledComponents';
import QuestionIcon16pxSvg from '../../../assets/images/icons/QuestionIcon16pxSvg';
import DefaultTooltip from '../../../components/DefaultTooltip';
import { ControlpanelPrimaryButton, ControlpanelSecondaryButton, GrayIconButton } from '../../../components/StyledButton';
import CloseIcon from "@mui/icons-material/Close";
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomInput from '../../../components/CustomInput';
import CustomDateRangePicker from '../../../components/CustomDateRangePicker';
import { useAuth } from '../../../context/AuthContext';
import CustomTimePicker from '../../../components/CustomTimePickerHaveSecond';
import CustomSelectRosterSelectUser from '../../../components/CustomSelectRosterSelectUser';
import { toast } from 'react-toastify';
import CustomDateTimeRangePicker from '../../../components/CustomDateTimeRangePicker';
import dayjs from "dayjs";
import { addEvents } from '../../../axios/Visitor/VisitorApi';
import { Language } from '@mui/icons-material';

const AddVisitorEventDialog = ({ open, handleClose, usersData, onRefresh }) => {
    const { t } = useTranslation();
    const { genaralSettingDateFormat, dateTimeFormat } = useAuth();

    const initialFormData = {
        name: '',
        description: '',
        startDate: null,
        endDate: null,
        startTime: null,
        endTime: null,
        isPublic: false,
        maxParticipants: 1,
        maxPerDay: 1,
        registrationStart: null,
        registrationEnd: null,
        staffInChargeIds: [],
        inviteEmails: '',
        address: '',
    };

    const [isValid, setIsValid] = useState(false);

    const [formData, setFormData] = useState(initialFormData);

    const isSameEventDay = useMemo(() => {
        if (!formData.startDate || !formData.endDate) return false;

        return dayjs(formData.startDate).isSame(
            dayjs(formData.endDate),
            "day"
        );
    }, [formData.startDate, formData.endDate]);

    const handleCloseDialog = () => {
        setFormData(initialFormData);
        handleClose();
    };

    const handleRemoveUser = (removedUserId) => {
        setFormData((prevData) => ({
            ...prevData,
            staffInChargeIds: prevData.staffInChargeIds.filter(id => id !== removedUserId),
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData((prevData) => {
            if (name === 'user') {
                return {
                    ...prevData,
                    staffInChargeIds: value,
                };
            }

            const nextData = {
                ...prevData,
                [name]: value,
            };

            // Cùng 1 ngày: maxPerDay luôn bằng maxParticipants
            if (name === "maxParticipants") {
                const sameDay =
                    prevData.startDate &&
                    prevData.endDate &&
                    dayjs(prevData.startDate).isSame(
                        dayjs(prevData.endDate),
                        "day"
                    );

                if (sameDay) {
                    nextData.maxPerDay = value;
                }
            }

            return nextData;
        });
    };

    useEffect(() => {
        const hasRequiredApprovers =
            formData.isPublic ||
            (formData.staffInChargeIds?.length > 0);

        const isFormValid =
            formData.name?.trim() !== '' &&
            formData.startDate &&
            formData.endDate &&
            formData.registrationStart &&
            formData.registrationEnd &&
            hasRequiredApprovers;

        setIsValid(isFormValid);
    }, [formData]);

    const handleSubmit = async () => {
        try {

            const payload = {
                ...formData,

                startDate: formData.startDate
                    ? dayjs(formData.startDate).format("YYYY-MM-DD")
                    : null,

                endDate: formData.endDate
                    ? dayjs(formData.endDate).format("YYYY-MM-DD")
                    : null,

                startTime: formData.startTime
                    ? formData.startTime
                    : "00:00:00",

                endTime: formData.endTime
                    ? formData.endTime
                    : "00:00:00",

                registrationStart: formData.registrationStart
                    ? dayjs(formData.registrationStart).toISOString()
                    : null,

                registrationEnd: formData.registrationEnd
                    ? dayjs(formData.registrationEnd)
                        .endOf("day")
                        .toISOString()
                    : null,

                langId: localStorage.getItem("i18nextLngId"),

                remark: formData.description,
            };

            const response = await addEvents(payload);

            // ===== SUCCESS =====
            if (
                response?.data?.code === 200
            ) {

                toast.success(
                    t("common.add-visitor-successfully") ||
                    "Add visitor successfully!"
                );

                handleCloseDialog();
                onRefresh();

            } else {

                toast.error(
                    response?.data?.data?.message ||
                    t("common.something-went-wrong")
                );
            }

        } catch (error) {

            console.error(error);

            toast.error(
                error?.response?.data?.message ||
                t("common.something-went-wrong") ||
                "Something went wrong"
            );
        }
    };

    return (
        <>
            <CustomDialog
                maxWidth={"sm"}
                open={open}
                onClose={handleCloseDialog}
                keepMounted
            >
                <DialogTitle>
                    <div className="flex justify-between">
                        <div className="flex items-center gap-4">
                            <p className="text-xl font-semibold ">{t("common.add-visitor")}</p>
                            <DefaultTooltip
                                readMore={true}
                                title={t("common.add-visitor")}
                                content={
                                    <div className="flex flex-col gap-3">
                                        <p>{t("common.add-visitor")}.</p>
                                    </div>
                                }
                            >
                                <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                            </DefaultTooltip>
                        </div>
                        <GrayIconButton onClick={handleCloseDialog}>
                            <CloseIcon />
                        </GrayIconButton>
                    </div>
                </DialogTitle>
                <DialogContent>
                    <div className="grid grid-cols-1 gap-x-10 gap-y-3 relative mt-4">
                        <div>
                            <CustomInput
                                required={true}
                                title={t("common.name")}
                                inputType="text"
                                name="name"
                                placeholder={t("common.name")}
                                value={formData.name}
                                handleInputChange={handleInputChange}
                            />
                        </div>

                        <div>
                            <CustomInput
                                required={true}
                                title={t("common.address")}
                                inputType="text"
                                name="address"
                                placeholder={t("common.address")}
                                value={formData.address}
                                handleInputChange={handleInputChange}
                            />
                        </div>

                        <div>
                            <CustomInput
                                required={true}
                                title={t("common.description")}
                                inputType="text"
                                name="description"
                                placeholder={t("common.description")}
                                value={formData.description}
                                handleInputChange={handleInputChange}
                            />
                        </div>

                        <div className='space-y-2'>
                            <label>
                                {t("common.start-date")} - {t("common.end-date")}
                                <span className="text-[#EF4444] ml-1">*</span>
                            </label>

                            <CustomDateTimeRangePicker
                                mode="range"
                                value={[
                                    formData.startDate && formData.startTime
                                        ? dayjs(
                                            `${dayjs(formData.startDate).format("YYYY-MM-DD")} ${formData.startTime}`,
                                            "YYYY-MM-DD HH:mm:ss"
                                        ).toDate()
                                        : null,

                                    formData.endDate && formData.endTime
                                        ? dayjs(
                                            `${dayjs(formData.endDate).format("YYYY-MM-DD")} ${formData.endTime}`,
                                            "YYYY-MM-DD HH:mm:ss"
                                        ).toDate()
                                        : null,
                                ]}
                                dateTimeFormat={dateTimeFormat}
                                disableShiftValidation={true}
                                onChange={(newValue) => {
                                    const start = newValue?.[0]
                                        ? dayjs(newValue[0])
                                        : null;

                                    const end = newValue?.[1]
                                        ? dayjs(newValue[1])
                                        : null;

                                    setFormData((prev) => {
                                        const nextData = {
                                            ...prev,

                                            startDate: start
                                                ? start.toDate()
                                                : null,

                                            endDate: end
                                                ? end.toDate()
                                                : null,

                                            startTime: start
                                                ? start.format("HH:mm:ss")
                                                : null,

                                            endTime: end
                                                ? end.format("HH:mm:ss")
                                                : null,
                                        };

                                        // Cùng 1 ngày: đồng bộ maxPerDay theo maxParticipants
                                        if (
                                            start &&
                                            end &&
                                            start.isSame(end, "day")
                                        ) {
                                            nextData.maxPerDay =
                                                prev.maxParticipants;
                                        }

                                        return nextData;
                                    });
                                }}
                            />
                        </div>

                        <div className='flex gap-2 items-center'>
                            <label>{t("common.public-event")}</label>
                            <NormalSwitch
                                checked={formData.isPublic}
                                onChange={(e) =>
                                    setFormData((p) => ({
                                        ...p,
                                        isPublic: e.target.checked,
                                    }))
                                }
                            />
                        </div>

                        <div className='space-y-2'>
                            <label htmlFor="startDate">
                                {t("common.registrationStart")} - {t("common.registrationEnd")}
                                <span className="text-[#EF4444] ml-1">*</span>
                            </label>

                            <CustomDateTimeRangePicker
                                mode="range"
                                value={[
                                    formData.registrationStart,
                                    formData.registrationEnd
                                ]}
                                dateTimeFormat={dateTimeFormat}
                                disableShiftValidation={true}
                                minDate={null} // cho phép chọn quá khứ
                                maxDate={formData.endDate}
                                onChange={(newValue) => {
                                    const start = newValue?.[0]
                                        ? dayjs(newValue[0])
                                        : null;

                                    const end = newValue?.[1]
                                        ? dayjs(newValue[1])
                                        : null;

                                    const maxAllowed = formData.endDate
                                        ? dayjs(formData.endDate).endOf("day")
                                        : null;

                                    setFormData((prev) => ({
                                        ...prev,
                                        registrationStart:
                                            start && maxAllowed && start.isAfter(maxAllowed)
                                                ? maxAllowed.toDate()
                                                : start?.toDate() || null,

                                        registrationEnd:
                                            end && maxAllowed && end.isAfter(maxAllowed)
                                                ? maxAllowed.toDate()
                                                : end?.toDate() || null,
                                    }));
                                }}
                            />
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                            <div>
                                <CustomInput
                                    required={true}
                                    title={t("common.maxParticipants")}
                                    inputType="number"
                                    name="maxParticipants"
                                    placeholder={t("common.maxParticipants")}
                                    value={formData.maxParticipants}
                                    minValue={1}
                                    handleInputChange={handleInputChange}
                                />
                            </div>

                            <div>
                                <CustomInput
                                    required={true}
                                    title={t("common.maxPerDay")}
                                    inputType="number"
                                    name="maxPerDay"
                                    placeholder={t("common.maxPerDay")}
                                    value={formData.maxPerDay}
                                    minValue={1}
                                    readOnly={isSameEventDay}
                                    handleInputChange={handleInputChange}
                                />
                            </div>
                        </div>

                        {formData.isPublic === false && (
                            <div>
                                <label>{t("common.approver")} <span className="text-[#EF4444] ml-1">*</span></label>
                                <CustomSelectRosterSelectUser
                                    multiple={true}
                                    name="user"
                                    value={formData.staffInChargeIds}
                                    onChange={handleInputChange}
                                    options={usersData}
                                    dataType="user"
                                    onRemoveItem={handleRemoveUser}
                                />
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <div className="flex gap-2 px-4 py-4">
                        <ControlpanelSecondaryButton onClick={handleCloseDialog}>
                            <p>{t("button.cancel")}</p>
                        </ControlpanelSecondaryButton>
                        <ControlpanelPrimaryButton
                            onClick={handleSubmit}
                            disabled={!isValid}
                        >
                            <p>{t("button.confirm")}</p>
                        </ControlpanelPrimaryButton>
                    </div>
                </DialogActions>
            </CustomDialog>
        </>
    )
}

export default AddVisitorEventDialog